import React, { memo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import ReactFlow, {
  Controls,
  Background,
  MiniMap,
  Panel,
  useReactFlow,
} from "reactflow";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { Icon as FontAwesomeIcon } from "orcs-design-system";
import "reactflow/dist/style.css";

import { useWorkflowBuilder } from "src/contexts/workflowBuilderContext";
import { nodeTypes } from "../nodes";
import { edgeTypes } from "../edges";

const CanvasContainer = styled.div`
  flex: 1;
  height: 100%;
  background: ${themeGet("colors.greyLightest")};
`;

const StyledReactFlow = styled(ReactFlow)`
  .react-flow__node {
    cursor: pointer;
  }

  .react-flow__edge {
    cursor: pointer;
  }

  .react-flow__controls {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    overflow: hidden;
  }

  .react-flow__controls-button {
    border: none;
    background: white;

    &:hover {
      background: ${themeGet("colors.greyLightest")};
    }
  }

  .react-flow__minimap {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const EmptyState = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
  z-index: 1;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${themeGet("colors.greyLight")};
  margin-bottom: 16px;
`;

const EmptyTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${themeGet("colors.greyDark")};
  margin-bottom: 8px;
`;

const EmptyDescription = styled.div`
  font-size: 14px;
  color: ${themeGet("colors.grey")};
  max-width: 300px;
`;

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border: 1px solid ${themeGet("colors.greyLighter")};
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: ${themeGet("colors.greyDark")};
  transition: all 0.2s ease;

  &:hover {
    background: ${themeGet("colors.greyLightest")};
    border-color: ${themeGet("colors.greyLight")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
`;

const proOptions = {
  account: "paid-pro",
  hideAttribution: true,
};

function WorkflowCanvas({ onSave, onPublish, onRun, isSaving, workflow }) {
  const reactFlowWrapper = useRef(null);
  const { screenToFlowPosition } = useReactFlow();
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    addNode,
    addEdge,
    selectNode,
    clearSelection,
    isDirty,
  } = useWorkflowBuilder();

  // Handle drag over for dropping new nodes (set dropEffect for DnD API, not param reassign)
  const onDragOver = useCallback((event) => {
    event.preventDefault();

    event.dataTransfer.dropEffect = "move";
  }, []);

  // Handle drop to add new node
  const onDrop = useCallback(
    (e) => {
      e.preventDefault();

      const data = e.dataTransfer.getData("application/reactflow");
      if (!data) {
        return;
      }

      const componentType = JSON.parse(data);
      const position = screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });

      addNode(componentType, position);
    },
    [screenToFlowPosition, addNode]
  );

  // Handle connection between nodes
  const onConnect = useCallback(
    (connection) => {
      addEdge(connection);
    },
    [addEdge]
  );

  // Handle node click
  const onNodeClick = useCallback(
    (_event, node) => {
      selectNode(node.id);
    },
    [selectNode]
  );

  // Handle pane click to clear selection
  const onPaneClick = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  // ReactFlow requires onNodeDragStop; position updates are handled by onNodesChange
  const onNodeDragStop = useCallback(() => {}, []);

  const isEmpty = nodes.length === 0;

  return (
    <CanvasContainer ref={reactFlowWrapper}>
      <StyledReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onNodeDragStop={onNodeDragStop}
        onDragOver={onDragOver}
        onDrop={onDrop}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        proOptions={proOptions}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        defaultEdgeOptions={{
          type: "workflowEdge",
          animated: false,
        }}
        connectionLineStyle={{ stroke: "#90A4AE", strokeWidth: 2 }}
        deleteKeyCode={["Backspace", "Delete"]}
      >
        <Background color="#ddd" gap={20} size={1} />

        <Controls position="bottom-left" />

        <MiniMap
          position="bottom-right"
          nodeColor={(node) => {
            const category = node.data?.category || "other";
            const colors = {
              extract: "#4CAF50",
              transform: "#2196F3",
              load: "#FF9800",
              control: "#9C27B0",
              other: "#607D8B",
            };
            return colors[category] || colors.other;
          }}
          maskColor="rgba(0,0,0,0.1)"
          pannable
          zoomable
        />

        <Panel position="top-right">
          <Toolbar>
            <ToolbarButton
              onClick={onSave}
              disabled={!isDirty || isSaving}
              title={isDirty ? "Save changes" : "No changes to save"}
            >
              <FontAwesomeIcon
                icon={["fas", isSaving ? "spinner" : "save"]}
                spin={isSaving}
              />
              {isSaving ? "Saving..." : "Save"}
            </ToolbarButton>

            {workflow?.status?.toLowerCase() !== "active" && (
              <ToolbarButton
                onClick={onPublish}
                disabled={isDirty || isSaving || nodes.length === 0}
                title={
                  isDirty
                    ? "Save changes before publishing"
                    : "Publish workflow"
                }
              >
                <FontAwesomeIcon icon={["fas", "rocket"]} />
                Publish
              </ToolbarButton>
            )}

            {workflow?.status?.toLowerCase() === "active" && onRun && (
              <ToolbarButton
                onClick={onRun}
                disabled={isSaving}
                title="Run workflow and view history"
                style={{ background: "#E3F2FD", borderColor: "#2196F3" }}
              >
                <FontAwesomeIcon
                  icon={["fas", "play"]}
                  style={{ color: "#2196F3" }}
                />
                <span style={{ color: "#1976D2" }}>Run</span>
              </ToolbarButton>
            )}
          </Toolbar>
        </Panel>

        {isEmpty && (
          <EmptyState>
            <EmptyIcon>
              <FontAwesomeIcon icon={["fas", "project-diagram"]} />
            </EmptyIcon>
            <EmptyTitle>Start building your workflow</EmptyTitle>
            <EmptyDescription>
              Drag components from the sidebar and connect them to create your
              data pipeline
            </EmptyDescription>
          </EmptyState>
        )}
      </StyledReactFlow>
    </CanvasContainer>
  );
}

WorkflowCanvas.propTypes = {
  onSave: PropTypes.func.isRequired,
  onPublish: PropTypes.func,
  onRun: PropTypes.func,
  isSaving: PropTypes.bool,
  workflow: PropTypes.object,
};

export default memo(WorkflowCanvas);
